import { FieldType } from '../../enums/field.type';
import { KeysTypeMetadataForm } from '../../enums/enum.keys.metadata.form';
import { FormField } from './form.field';
import { FormSection } from './form.section';
import { FormCode } from './form.template';

/**
 * The template paths whose picker offers the merged entity's own lines. Their answer must always be
 * stored as the line object, whatever the capturing surface handed back.
 */
const LINE_PICKER_PATHS: string[] = [KeysTypeMetadataForm.WorkOrderCodes, KeysTypeMetadataForm.QuoteCodes, KeysTypeMetadataForm.JobCodes];

type LinePickerTemplate = { pages?: { name: string; sections?: FormSection[] }[]; variables?: Record<string, any> };

/**
 * Stage / Quote Items answers arrive in different shapes — the web stores the picked line object, the
 * apps hand back the line id (and older app builds the line name). Downstream readers (PDF merge, reports,
 * integrations) want ONE shape, so the save path runs every such answer through here and stores the
 * line object from the template's variables.
 *
 * Unknown values are left untouched rather than dropped: a value that matches no line is still the
 * user's answer, and the reader's existing fallbacks handle it as they do today.
 */
export function normaliseLinePickerAnswers<T>(template: LinePickerTemplate, response: T): T {

    if (!template?.pages || !response) return response;

    for (const page of template.pages) {
        const pageAnswers = response[page.name];
        if (!pageAnswers) continue;

        for (const section of page.sections ?? []) {
            const pickers = (section.fields ?? []).filter(isLinePickerField);
            if (!pickers.length) continue;

            const sectionAnswers = pageAnswers[section.name];
            const rows: any[] = section.options?.repeat
                ? (Array.isArray(sectionAnswers) ? sectionAnswers : [])
                : (sectionAnswers ? [sectionAnswers] : []);

            for (const row of rows) {
                for (const picker of pickers) {
                    if (!(picker.name in row)) continue;
                    const lines: FormCode[] = template.variables?.[picker.options.path] ?? [];
                    row[picker.name] = resolveAnswer(row[picker.name], lines);
                }
            }
        }
    }
    return response;
}

function isLinePickerField(field: FormField): boolean {

    return field?.type === FieldType.InfoField && LINE_PICKER_PATHS.includes(field.options?.path);
}

function resolveAnswer(answer: unknown, lines: FormCode[]): unknown {

    if (Array.isArray(answer)) return answer.map(entry => resolveOne(entry, lines));
    return resolveOne(answer, lines);
}

function resolveOne(answer: unknown, lines: FormCode[]): unknown {

    if (typeof answer === 'string') {
        return lines.find(line => line._id === answer) ?? uniqueByName(answer, lines) ?? answer;
    }
    if (answer && typeof answer === 'object') {
        const picked = answer as FormCode;
        if (picked._id) return lines.find(line => line._id === picked._id) ?? picked;
        //A line object saved before ids travelled with it: adopt the id when the name (and location) single it out.
        const candidates = lines.filter(line => line.name === picked.name && (picked.location == null || line.location === picked.location));
        return candidates.length === 1 ? candidates[0] : picked;
    }
    return answer;
}

function uniqueByName(name: string, lines: FormCode[]): FormCode | undefined {

    const candidates = lines.filter(line => line.name === name);
    return candidates.length === 1 ? candidates[0] : undefined;
}
